import React, { useState } from "react";
import { LabeledInput } from "@/Components/Form";
import { Inertia } from "@inertiajs/inertia";
import { FormError, DateSelectDay, Formheader } from "@/Components";
import { MapPin, Trash, X } from "react-feather";
import { Info } from "@/Components/Info";
import { ROLE_ID } from "@/Enums/roles";
import { LabeledPlacesAutocomplete } from "@/Components/PlacesAutocomplete";
import { toast } from "react-toastify";
import AddReporterToTeamSidebar from "@/Pages/Teams/Partials/AddReporterToTeamSidebar";
import { usePage } from "@inertiajs/inertia-react";

export function TeamSettings({ errors, team }) {
  const errorBagKey = "updateTeam." + team.id;
  const { users } = usePage()?.props || {};

  let [modal, setModal] = useState({
    open: false,
    title: "",
    content: "",
    class: "",
    callback: null,
    callbackText: "",
  });
  let [isAddUserOpen, setIsAddUserOpen] = useState(false);
  let [dirty, setDirty] = useState(false);
  let [values, setValues] = useState({
    name: team.name || "",
    equity: team.equity || 100,
    report_frequency: team.report_frequency || 1,
    end_date: team.end_date || null,
    location: team.location || "",
    location_place_id: team.location_place_id || null,
    users: team.users.map((u) => u.id),
  });

  function handleSave(e) {
    e.preventDefault();

    if (!dirty) {
      return;
    }

    let data = {
      name: values.name,
      equity: values.equity,
      report_frequency: values.report_frequency,
      location: values.location,
      location_place_id: values.location_place_id,
      users: values.users || [],
    };

    // End date is optional
    if (values.end_date) {
      data.end_date = values.end_date;
    }

    const id = toast.loading("Please wait...");

    Inertia.put(`/teams/${team.id}`, data, {
      preserveScroll: true,
      onSuccess: () => {
        toast.update(id, {
          render: "The team was successfully updated!",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
        setDirty(false);
      },
      onError: () => {
        toast.update(id, {
          render: "An error occurred while updating the team",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  }

  function handleUpdate(event) {
    setValues((prevValues) => {
      return { ...prevValues, [event.target.name]: event.target.value };
    });
    setDirty(true);
  }

  function openModal(t, d, c, cb = null, cbText = "") {
    setModal({
      open: true,
      title: t,
      content: d,
      class: c,
      callback: cb,
      callbackText: cbText,
    });
  }

  function closeModal() {
    setModal({
      open: false,
      title: "",
      content: "",
      class: "",
      callback: null,
      callbackText: "",
    });
  }

  function handleDelete(forced = false) {
    Inertia.delete(`/teams/${team.id}${forced ? "/force" : ""}`, {
      preserveScroll: true,
      onFinish: () => {
        if (!forced) {
          closeModal();
          return;
        }

        Inertia.visit("/teams");
      },
    });
  }

  function handleRestore() {
    Inertia.post(`/teams/${team.id}/restore`, null, {
      onFinish: () => {
        closeModal();
      },
    });
  }

  function handleDeleteUser(user_id) {
    setDirty(true);
    setValues((prevValues) => ({
      ...prevValues,
      users: prevValues.users.filter((u) => u !== user_id),
    }));
  }

  function setDate(date, property) {
    setDirty(true);
    setValues((prevValues) => ({
      ...prevValues,
      [property]: date,
    }));
  }

  function setLocation({ description, place_id }) {
    setDirty(true);
    setValues((prevValues) => ({
      ...prevValues,
      location: description,
      location_place_id: place_id,
    }));
  }

  function SettingsSaveButton() {
    let disabled = false;

    if (!dirty) {
      disabled = true;
    }

    return (
      <button
        disabled={disabled}
        onClick={handleSave}
        className="btn ml-3 self-end"
      >
        Save
      </button>
    );
  }

  return (
    <>
      <form onSubmit={handleSave} className="flex flex-col gap-4">
        <div className="shadow-small flex flex-wrap bg-gray-light px-8 py-4">
          <div className="w-full">
            <Formheader>Team details</Formheader>
          </div>

          <div className={`mb-4 w-full md:w-1/2 md:pr-3`}>
            <LabeledInput
              title="Team name"
              name="name"
              type="text"
              placeholder="Enter team name"
              value={values.name}
              onChange={handleUpdate}
            />
            <FormError error={errors && errors[errorBagKey]?.name} />
          </div>

          <div className="mb-4 w-full md:w-1/2">
            <LabeledInput
              unit="%"
              title={
                <div className="flex gap-2">
                  <span>Consolidation approach</span>
                  <Info>
                    The consolidation approach for emissions from teams can be
                    based on either equity share, financial- or operational
                    control. This percentage reflects this teams level of
                    control and, consequently, the extent to which the teams
                    emissions are consolidated into your total emissions
                    footprint. Specify with the percentage ranging from 0 to
                    100%.
                  </Info>
                </div>
              }
              placeholder="Enter consolidation approach amount"
              name="equity"
              required="required"
              type="number"
              min="0"
              max="100"
              value={values.equity}
              onChange={handleUpdate}
            />
            <FormError error={errors && errors[errorBagKey]?.equity} />
          </div>

          <div className="relative mb-4 w-full pr-3 md:w-1/2">
            <LabeledPlacesAutocomplete
              title="Geographic location"
              placeholder="Enter name of town or region"
              className="w-full pl-10"
              name="location"
              defaultValue={values.location}
              onChange={setLocation}
            />
            <MapPin size={18} className="absolute left-3 -mt-8" />
            <FormError error={errors && errors[errorBagKey]?.location} />
            <FormError
              error={errors && errors[errorBagKey]?.location_place_id}
            />
          </div>

          <div className="mb-4 w-full md:w-1/2">
            <label>
              <span className="mb-1 block text-[14px]">End date</span>
            </label>
            <DateSelectDay
              onChange={(date) => setDate(date, "end_date")}
              value={values.end_date}
              buttons={["clear", "today"]}
            />
            <FormError error={errors && errors[errorBagKey]?.end_date} />
          </div>
        </div>

        <div className="shadow-small flex flex-wrap bg-gray-light px-8 py-4">
          <div className="w-full">
            <Formheader>Reporters</Formheader>
          </div>

          {values.users &&
            values.users.map((u, i) => {
              const user = users.data.find((user) => user.id === u);

              return (
                <div
                  key={i}
                  className="mb-2 flex w-full justify-between border border-gray-lighter bg-white px-4 py-2 shadow-sm"
                >
                  <div>
                    {user.organization_role_id === ROLE_ID.OWNER && (
                      <img
                        className="-mt-1 mr-2 inline w-[20px]"
                        src={`/images/diamond.svg`}
                      />
                    )}
                    {user.firstname && user.surname ? (
                      <span className="font-bold">{user.fullname}</span>
                    ) : (
                      user.email
                    )}
                  </div>

                  <span className="hover-circle relative">
                    <Trash
                      className="cursor-pointer"
                      onClick={() => handleDeleteUser(user.id)}
                    />
                  </span>
                </div>
              );
            })}

          <div className="mb-4 w-full">
            {team?.users?.length === 0 && (
              <p className="text-sm text-gray-600">
                No reporters have been added to this team
                <br />
                Click "Add reporter" to select your reporter(s)
              </p>
            )}

            <button
              className="btn-blue mt-2"
              onClick={() => setIsAddUserOpen(!isAddUserOpen)}
              type="button"
            >
              Add reporters
            </button>
          </div>

          <div className="mb-4 w-full">
            <p>How often should reporters get reminders to report?</p>
            <div className="mt-2 flex flex-col gap-2 md:flex-row md:gap-4">
              <label>
                <input
                  checked={Number(values.report_frequency) === 0}
                  value={0}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">No reports</span>
              </label>

              <label>
                <input
                  checked={Number(values.report_frequency) === 1}
                  value={1}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Monthly</span>
              </label>

              <label>
                <input
                  checked={Number(values.report_frequency) === 3}
                  value={3}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Quarterly</span>
              </label>
              <label>
                <input
                  checked={Number(values.report_frequency) === 12}
                  value={12}
                  type="radio"
                  name="report_frequency"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Yearly</span>
              </label>
            </div>
            <FormError
              error={errors && errors[errorBagKey]?.report_frequency}
            />
          </div>
        </div>

        <div className="mt-2 flex w-full items-center justify-end">
          {team.is_deleted && (
            <a
              className="cursor-pointer py-2 text-blue underline"
              onClick={() => {
                openModal(
                  "Restore team",
                  `You're about to restore ${team.name}.\n Do you want to continue?`,
                  "",
                  () => handleRestore(),
                  "Restore team",
                );
              }}
            >
              Restore
            </a>
          )}

          {!team.is_deleted && (
            <a
              className="cursor-pointer py-2 text-blue underline"
              onClick={() => {
                openModal(
                  "Inactivate team",
                  `You're about to inactivate ${team.name}.\n Do you want to continue?`,
                  "",
                  () => handleDelete(),
                  "Inactivate team",
                );
              }}
            >
              Inactivate
            </a>
          )}

          <a
            className="ml-3 cursor-pointer py-2 text-blue underline"
            onClick={() => {
              openModal(
                "Delete team",
                `You're about to delete ${team.name}.\n Do you want to continue?`,
                "",
                () => {
                  handleDelete(true);
                },
                "Delete team",
              );
            }}
          >
            Delete
          </a>

          <SettingsSaveButton />
        </div>
      </form>

      {modal.open && (
        <div className="fixed bottom-0 left-0 right-0 top-0 z-20 flex flex-col justify-center bg-black bg-opacity-50">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={closeModal}
          />
          <div className="relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16">
            <X
              className="absolute right-0 top-0 h-12 w-12 cursor-pointer p-2"
              onClick={closeModal}
            />
            <h3 className="mb-3 text-xl font-semibold">{modal.title}</h3>
            <p>{modal.content}</p>

            <div className="mt-5 flex w-full justify-end border-t pt-8">
              <a
                className="cursor-pointer py-4 text-blue underline"
                onClick={closeModal}
              >
                Cancel
              </a>

              {modal.callback && (
                <button className="btn ml-3 self-end" onClick={modal.callback}>
                  {modal.callbackText}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <AddReporterToTeamSidebar
        openModal={isAddUserOpen}
        setOpenModal={setIsAddUserOpen}
        values={values}
        setValues={(val) => {
          setDirty(true);
          setValues(val);
        }}
      />
    </>
  );
}
